/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import {
  Box,
  Card,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import Swal from 'sweetalert2';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import MDDatePicker from 'components/MDDatePicker';
import LoaderOverlay from 'components/LoaderOverlay';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import { api } from '../../api';
import { usePermissions } from '../../hooks/usePermissions';
import {
  DROPDOWN_SX,
  STATUS_LABELS,
  STATUS_LIST,
  TEAL,
  TEAL_BORDER,
  TEAL_LIGHT,
  getCurrentUserId,
  getDefaultDateRange,
  getMinAllowedStartDate,
} from './constants';
import LifecycleRibbon from './components/LifecycleRibbon';
import MonthYearPicker from './components/MonthYearPicker';
import AppointmentCalendarView from './components/AppointmentCalendarView';
import AppointmentListView from './components/AppointmentListView';
import AppointmentKanbanBoard from './components/AppointmentKanbanBoard';
import DoctorSerialView from './components/DoctorSerialView';
import AppointmentDetailDrawer from './components/AppointmentDetailDrawer';
import CreateAppointmentDialog from './components/CreateAppointmentDialog';
import CancelDialog from './components/CancelDialog';

const VIEWS = [
  { value: 'calendar', label: 'Calendar' },
  { value: 'list', label: 'List' },
  { value: 'kanban', label: 'Board' },
  { value: 'serial', label: 'Doctor Serial' },
];

const ADMIN_LIST_LIMIT = 300;

export default function OPDAppointments() {
  const { hasPermission } = usePermissions();

  const [view, setView] = useState('calendar');
  const [monthDate, setMonthDate] = useState(new Date());
  // Shared by List + Board (Kanban): defaults to today only, expandable up to 6 months back.
  const [dateRange, setDateRange] = useState(getDefaultDateRange());
  const [doctorFilter, setDoctorFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [patientSearch, setPatientSearch] = useState('');

  const [doctors, setDoctors] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [calendarData, setCalendarData] = useState({});
  const [loading, setLoading] = useState(false);

  const [serialDoctorId, setSerialDoctorId] = useState('');
  const [serialDate, setSerialDate] = useState(dayjs().format('YYYY-MM-DD'));

  const [createOpen, setCreateOpen] = useState(false);
  const [detailAppointmentId, setDetailAppointmentId] = useState(null);
  const [kanbanCancelTarget, setKanbanCancelTarget] = useState(null);

  useEffect(() => {
    api.contacts
      .getDoctorsList()
      .then(resp => setDoctors(resp?.data || []))
      .catch(() => setDoctors([]));
  }, []);

  // Restore a date-range (+ optional doctor/status) drill-down from the URL
  // (e.g. clicking the appointment-count widget on the Dashboard) — runs
  // once on mount.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const dateFrom = params.get('date_from');
    const dateTo = params.get('date_to');
    if (!dateFrom || !dateTo) return;
    const minAllowed = getMinAllowedStartDate(dateTo);
    setDateRange({ start: dateFrom < minAllowed ? minAllowed : dateFrom, end: dateTo });
    const doctorIdParam = params.get('doctor_id');
    if (doctorIdParam) setDoctorFilter(Number(doctorIdParam));
    const statusParam = params.get('status');
    if (statusParam) setStatusFilter(statusParam);
    setView('list');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStartDateChange = newStart => {
    if (!newStart) return;
    setDateRange(prev => {
      const minAllowed = getMinAllowedStartDate(prev.end);
      const clamped = newStart < minAllowed ? minAllowed : newStart;
      return { ...prev, start: clamped };
    });
  };

  const handleEndDateChange = newEnd => {
    if (!newEnd) return;
    setDateRange(prev => {
      const minAllowed = getMinAllowedStartDate(newEnd);
      const start = prev.start < minAllowed ? minAllowed : prev.start;
      return { start, end: newEnd };
    });
  };

  const loadListAndKanban = useCallback(() => {
    setLoading(true);
    api.appointment
      .adminListAppointments({
        start_date: dateRange.start,
        end_date: dateRange.end,
        doctor_id: doctorFilter || undefined,
        status: statusFilter || undefined,
        limit: ADMIN_LIST_LIMIT,
      })
      .then(resp => setAppointments(resp?.data || []))
      .catch(() => setAppointments([]))
      .finally(() => setLoading(false));
  }, [dateRange, doctorFilter, statusFilter]);

  const loadCalendar = useCallback(() => {
    api.appointment
      .getCalendar({
        month: dayjs(monthDate).format('YYYY-MM'),
        doctor_id: doctorFilter || undefined,
        status: statusFilter || undefined,
      })
      .then(resp => setCalendarData(resp?.data || {}))
      .catch(() => setCalendarData({}));
  }, [monthDate, doctorFilter, statusFilter]);

  useEffect(() => {
    loadListAndKanban();
  }, [loadListAndKanban]);

  useEffect(() => {
    loadCalendar();
  }, [loadCalendar]);

  const [serialAppointments, setSerialAppointments] = useState([]);
  const loadSerial = useCallback(() => {
    if (!serialDate) return;
    api.appointment
      .adminListAppointments({
        start_date: serialDate,
        end_date: serialDate,
        doctor_id: serialDoctorId || undefined, // empty = All Doctors
        limit: ADMIN_LIST_LIMIT,
      })
      .then(resp => setSerialAppointments((resp?.data || []).sort((a, b) => a.serial_no - b.serial_no)))
      .catch(() => setSerialAppointments([]));
  }, [serialDoctorId, serialDate]);

  useEffect(() => {
    if (view === 'serial') loadSerial();
  }, [view, loadSerial]);

  const refreshAll = () => {
    loadListAndKanban();
    loadCalendar();
    if (view === 'serial') loadSerial();
  };

  const patientFilteredAppointments = useMemo(() => {
    if (!patientSearch.trim()) return appointments;
    const term = patientSearch.trim().toLowerCase();
    return appointments.filter(a => {
      const name = (a.contactInfo?.first_name || '') + ' ' + (a.contactInfo?.last_name || '');
      return name.toLowerCase().includes(term);
    });
  }, [appointments, patientSearch]);

  const handleKanbanStatusChange = async (id, from, to) => {
    const appointment = appointments.find(a => a.id === id);
    if (to === 'CANCELLED') {
      setKanbanCancelTarget(appointment);
      return;
    }
    const confirmResult = await Swal.fire({
      title: `Move to ${STATUS_LABELS[to]}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: TEAL,
      confirmButtonText: 'Yes, Confirm',
      cancelButtonText: 'Cancel',
    });
    if (!confirmResult.isConfirmed) return;

    const updatedBy = getCurrentUserId();
    try {
      if (to === 'CHECKED_IN') await api.appointment.checkIn(id, { updated_by: updatedBy });
      else if (to === 'ATTENDED' && from === 'SCHEDULED') await api.appointment.attendDirect(id, { updated_by: updatedBy });
      else if (to === 'ATTENDED') await api.appointment.attend(id, { updated_by: updatedBy });
      else if (to === 'COMPLETED') await api.appointment.complete(id, { updated_by: updatedBy });
      refreshAll();
    } catch {
      // generic error toast already shown by ApiClient
    }
  };

  const handleReorder = async (doctorId, orderedIds) => {
    try {
      await api.appointment.reorderSerial(doctorId, serialDate, {
        ordered_ids: orderedIds,
        updated_by: getCurrentUserId(),
      });
      loadSerial();
    } catch {
      loadSerial();
    }
  };

  const serialDoctorInfo = useMemo(
    () => doctors.find(d => d.user_id === Number(serialDoctorId)),
    [doctors, serialDoctorId],
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <LoaderOverlay open={loading} />
      <LifecycleRibbon />

      <MDBox pt={6} pb={3}>
        <Card>
          <MDBox mx={2} mt={-3} py={2} px={2} variant="gradient" bgColor="logo" borderRadius="lg" coloredShadow="logo"
            display="flex" alignItems="center" justifyContent="space-between">
            <MDTypography variant="h6" color="white">OPD Appointments</MDTypography>
            {hasPermission('v1-opd-appointment-create') && (
              <MDButton
                size="small"
                variant="gradient"
                color="white"
                startIcon={<AddIcon />}
                onClick={() => setCreateOpen(true)}
                sx={{
                  '&:hover': { backgroundColor: 'white', boxShadow: 'none' },
                  '&:focus, &:focus-visible': { backgroundColor: 'white', boxShadow: 'none', outline: 'none' },
                }}
              >
                New Appointment
              </MDButton>
            )}
          </MDBox>

          <MDBox p={3}>
            <Box
              sx={{
                display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 2,
                p: 1.75, bgcolor: TEAL_LIGHT, borderRadius: 2, border: `1px solid ${TEAL_BORDER}`,
              }}
            >
              {view === 'calendar' ? (
                <Box sx={{ minWidth: 160 }}>
                  <MonthYearPicker label="Jump to month" value={monthDate} onChange={setMonthDate} />
                </Box>
              ) : view !== 'serial' && (
                <>
                  <Box sx={{ minWidth: 150 }}>
                    <MDDatePicker label="From" value={dateRange.start} onChange={handleStartDateChange} />
                  </Box>
                  <Box sx={{ minWidth: 150 }}>
                    <MDDatePicker label="To" value={dateRange.end} onChange={handleEndDateChange} />
                  </Box>
                </>
              )}

              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel id="appt-doctor-filter-label">Doctor</InputLabel>
                <Select
                  labelId="appt-doctor-filter-label"
                  label="Doctor"
                  value={doctorFilter}
                  onChange={e => setDoctorFilter(e.target.value)}
                  sx={DROPDOWN_SX}
                >
                  <MenuItem value="">All Doctors</MenuItem>
                  {doctors.map(d => (
                    <MenuItem key={d.user_id} value={d.user_id}>{d.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel id="appt-status-filter-label">Status</InputLabel>
                <Select
                  labelId="appt-status-filter-label"
                  label="Status"
                  value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value)}
                  sx={DROPDOWN_SX}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  {STATUS_LIST.map(s => (
                    <MenuItem key={s} value={s}>{STATUS_LABELS[s]}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                size="small"
                label="Filter by patient name"
                value={patientSearch}
                onChange={e => setPatientSearch(e.target.value)}
                sx={{ minWidth: 200, '& .MuiOutlinedInput-root': { minHeight: 36 } }}
              />

              <MDButton variant="gradient" color="logo" onClick={refreshAll} sx={{ ml: 'auto' }}>
                Refresh
              </MDButton>
            </Box>

            <Tabs value={view} onChange={(_, v) => setView(v)} sx={{ mb: 2 }}>
              {VIEWS.map(v => <Tab key={v.value} value={v.value} label={v.label} />)}
            </Tabs>

            {view === 'calendar' && (
              <AppointmentCalendarView
                calendarData={calendarData}
                currentDate={monthDate}
                onNavigate={date => setMonthDate(date)}
                onSelectEvent={setDetailAppointmentId}
              />
            )}

            {view === 'list' && (
              <AppointmentListView appointments={patientFilteredAppointments} onSelect={setDetailAppointmentId} onChanged={refreshAll} />
            )}

            {view === 'kanban' && (
              <AppointmentKanbanBoard
                appointments={patientFilteredAppointments}
                onSelect={setDetailAppointmentId}
                onStatusChange={handleKanbanStatusChange}
              />
            )}

            {view === 'serial' && (
              <Box>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel id="serial-doctor-label">Doctor</InputLabel>
                    <Select
                      labelId="serial-doctor-label"
                      label="Doctor"
                      value={serialDoctorId}
                      onChange={e => setSerialDoctorId(e.target.value)}
                      sx={DROPDOWN_SX}
                    >
                      <MenuItem value="">All Doctors</MenuItem>
                      {doctors.map(d => (
                        <MenuItem key={d.user_id} value={d.user_id}>{d.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <MDDatePicker label="Date" value={serialDate} onChange={setSerialDate} />
                </Box>
                <DoctorSerialView
                  appointments={serialAppointments}
                  doctorInfo={serialDoctorInfo}
                  doctorId={serialDoctorId}
                  onSelect={setDetailAppointmentId}
                  onChanged={refreshAll}
                  onReorder={handleReorder}
                />
              </Box>
            )}
          </MDBox>
        </Card>
      </MDBox>

      <AppointmentDetailDrawer
        appointmentId={detailAppointmentId}
        onClose={() => setDetailAppointmentId(null)}
        onChanged={refreshAll}
      />

      <CreateAppointmentDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={refreshAll} />

      {kanbanCancelTarget && (
        <CancelDialog
          appointment={kanbanCancelTarget}
          onClose={() => setKanbanCancelTarget(null)}
          onDone={() => {
            setKanbanCancelTarget(null);
            refreshAll();
          }}
        />
      )}

      <Footer />
    </DashboardLayout>
  );
}
